import * as React from "react";

import type { AsyncAnswerStatus } from "@/hooks/useAsyncSessionTracker";
import type { AskNeedlTools } from "@/types/api";
// Import the types we need for the interfaces
import type { Message, ToolCallLog } from "@/types/chat";

// Re-export types from shared types file
export type { Message, ToolCallLog } from "@/types/chat";

// Simplified chat context state interface - sessions removed
export interface ChatContextState {
  sessionId: string;
  messages: Message[];
  streamingAnswer: string;
  isLoading: boolean;
  isStreaming: boolean;
  isError: boolean;
  input: string;
  toolCallLogs: ToolCallLog[];
  tools: AskNeedlTools | null;
  citationGenerationStarted: boolean;
  reportType: string | null;
  sessionTitle: string | null;
  asyncStatuses: Map<string, AsyncAnswerStatus>;
  isArtifactReportSchedulingMessageDisabled: boolean;
}

// Simplified chat context actions interface - session management removed
export interface ChatContextActions {
  disableArtifactReportSchedulingMessage: () => void;
  markReportScheduled: (reportId: string) => void;
  setIsArtifactReportSchedulingMessageDisabled: (disabled: boolean) => void;
  sendMessage: (message: string) => Promise<void>;
  incrementVoiceCount: () => void;
  updateInput: (input: string) => void;
  clearConversation: () => void;
  handleChoiceSelect: (choice: string) => void;
  setSessionId: (sessionId: string) => void;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  setTools: (tools: AskNeedlTools | null) => void;
  setReportType: (reportType: string | null) => void;
  setSessionTitle: (title: string | null) => void;
  initializeToolsFromSession: (sessionTools?: AskNeedlTools) => void;
  stopRequest: () => void;
  initializeChatState: () => void;
  hideMessage: (messageId: string) => void;
}

// Combined context type
export type ChatContextType = ChatContextState & ChatContextActions;

// Create the context with an undefined initial value
export const ChatContext = React.createContext<ChatContextType | undefined>(
  undefined
);
